import React, { memo, useCallback, useRef, useState } from "react"
import { LoaderCircleIcon, SaveIcon, PlusIcon, TrashIcon, ChevronDown, ChevronRight, XCircleIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
export interface PeriodEnumeration {
  name: string
  iconType: string
  icon: string
  color: string
  sequence: number
}

export interface PeriodOption {
  name: string
  type: "manual" | "ioas"
  sequence: number
  enumeration: PeriodEnumeration[]
}

export interface InstanceStyleValue {
  value: string
  color: string
  background: string
}

export interface InstanceIcon {
  type: string
  position: string
  displayIfZero: boolean
  recordCount: string
  backgroundColor: string
  color: string
}
export interface IconConfig {
  value: string
  iconType: "svg"
  icon: string
  color: string
}

export interface TimelineUpperIcons {
  autoTag: Record<string, IconConfig[]>
  userTag: Record<string, IconConfig[]>
  evidenceCounts: IconConfig[]
}

export interface TimelineConfig {
  general: {
    useUpper: boolean
    useLower: boolean
    useInstances: boolean
  }
  upper: {
    allowManualPeriodDateAdjustment: boolean
    allowManualPeriodOptionsAdjustment: boolean
    periodNameSingular: string
    periodNamePlural: string
    periodOverlapMonths: number
    periodOverlapMonthsToEOM: boolean
    disableOverlapMonths: boolean
    periodOptions: PeriodOption[]
    icons: TimelineUpperIcons
  }
  lower: {
    showRecordClusters: boolean
  }
  instances: {
    showSequenceNumber: boolean
    instanceStyle: {
      defaultBackground: string
      defaultColour: string
      type: string
      instanceTag: string
      values: InstanceStyleValue[]
    }
    instanceIcons: InstanceIcon[]
  }
}

export interface SharedTimelineTabProps {
  timelineConfig: TimelineConfig
  onConfigChange: (config: TimelineConfig) => void
  onSave: (config: TimelineConfig) => Promise<void> | void
  onRemove: () => Promise<void> | void
  isFetching: boolean
  currentAction?: "SAVE_SECTION" | "REMOVE_SECTION" | null
  evidenceCountLabels?: string[]
  isLoadingLabels?: boolean
}

const AddPeriodOptionDialog = memo(({ onAdd }: { onAdd: (option: PeriodOption) => void }) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [open, setOpen] = useState(false)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (formRef.current == null) return

      const formData = new FormData(formRef.current)
      const name = formData.get("name") as string
      const type = formData.get("type") as "manual" | "ioas"
      const sequence = parseInt(formData.get("sequence") as string) || 0

      if (name.trim()) {
        onAdd({ name, type, sequence, enumeration: [] })
        formRef.current.reset()
        setOpen(false)
      }
    },
    [onAdd],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="border-green-200 bg-green-50 hover:bg-green-100">
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Period Option
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Period Option</DialogTitle>
        </DialogHeader>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Name</label>
            <Input name="name" placeholder="Option name" className="mt-1" required />
          </div>

          <div>
            <label className="text-sm font-medium">Type</label>
            <Select name="type" defaultValue="manual">
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="ioas">IOAS</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Sequence</label>
            <Input type="number" name="sequence" defaultValue="0" min="0" className="mt-1" required />
          </div>

          <div className="flex items-center justify-end gap-4 border-t pt-4">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Option</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
})
AddPeriodOptionDialog.displayName = "AddPeriodOptionDialog"

const AddEnumerationDialog = memo(({ onAdd }: { onAdd: (enumeration: PeriodEnumeration) => void }) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [open, setOpen] = useState(false)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (formRef.current == null) return

      const formData = new FormData(formRef.current)
      const name = formData.get("name") as string
      const iconType = formData.get("iconType") as string
      const icon = formData.get("icon") as string
      const color = formData.get("color") as string
      const sequence = parseInt(formData.get("sequence") as string) || 0

      if (name.trim()) {
        onAdd({ name, iconType, icon, color, sequence })
        formRef.current.reset()
        setOpen(false)
      }
    },
    [onAdd],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="border-info/30 bg-info/10 hover:bg-info/20">
          <PlusIcon className="mr-1 h-3 w-3" />
          Add Enumeration
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Period Enumeration</DialogTitle>
        </DialogHeader>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Name</label>
            <Input name="name" placeholder="Enumeration name" className="mt-1" required />
          </div>

          <div>
            <label className="text-sm font-medium">Icon Type</label>
            <Select name="iconType" defaultValue="character">
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="character">Character</SelectItem>
                <SelectItem value="svg">SVG</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Icon</label>
            <Input name="icon" placeholder="Character or SVG path" className="mt-1" />
          </div>

          <div>
            <label className="text-sm font-medium">Color</label>
            <input
              type="color"
              name="color"
              defaultValue="#000000"
              className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Sequence</label>
            <Input type="number" name="sequence" defaultValue="0" min="0" className="mt-1" required />
          </div>

          <div className="flex items-center justify-end gap-4 border-t pt-4">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Enumeration</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
})
AddEnumerationDialog.displayName = "AddEnumerationDialog"

const AddInstanceStyleValueDialog = memo(({ onAdd }: { onAdd: (value: InstanceStyleValue) => void }) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [open, setOpen] = useState(false)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (formRef.current == null) return

      const formData = new FormData(formRef.current)
      const value = formData.get("value") as string
      const color = formData.get("color") as string
      const background = formData.get("background") as string

      if (value.trim()) {
        onAdd({ value, color, background })
        formRef.current.reset()
        setOpen(false)
      }
    },
    [onAdd],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-green-200 bg-green-50 hover:bg-green-100 dark:border-green-800 dark:bg-green-950 dark:hover:bg-green-900"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Style Value
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Instance Style Value</DialogTitle>
        </DialogHeader>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Value</label>
            <Input name="value" placeholder="Style value" className="mt-1" required />
          </div>

          <div>
            <label className="text-sm font-medium">Text Color</label>
            <input
              type="color"
              name="color"
              defaultValue="#000000"
              className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Background Color</label>
            <input
              type="color"
              name="background"
              defaultValue="#ffffff"
              className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
            />
          </div>

          <div className="flex items-center justify-end gap-4 border-t pt-4">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Value</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
})
AddInstanceStyleValueDialog.displayName = "AddInstanceStyleValueDialog"

const AddInstanceIconDialog = memo(
  ({
    onAdd,
    evidenceCountLabels = [],
    isLoadingLabels = false,
  }: {
    onAdd: (icon: InstanceIcon) => void
    evidenceCountLabels?: string[]
    isLoadingLabels?: boolean
  }) => {
    const formRef = useRef<HTMLFormElement>(null)
    const [open, setOpen] = useState(false)
    const isAdvancedMode = evidenceCountLabels.length > 0 || isLoadingLabels
    const [selectedRecordCount, setSelectedRecordCount] = useState("")

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)

        if (isAdvancedMode) {
          // Brand Lens mode
          const icon: InstanceIcon = {
            type: "recordCount",
            position: formData.get("position") as string,
            displayIfZero: formData.get("displayIfZero") === "on",
            recordCount: selectedRecordCount,
            backgroundColor: formData.get("backgroundColor") as string,
            color: formData.get("color") as string,
          }
          onAdd(icon)
        } else {
          //(UIConfig)
          const icon: InstanceIcon = {
            type: formData.get("type") as string,
            position: formData.get("position") as string,
            displayIfZero: formData.get("displayIfZero") === "on",
            recordCount: formData.get("recordCount") as string,
            backgroundColor: formData.get("backgroundColor") as string,
            color: formData.get("color") as string,
          }
          onAdd(icon)
        }

        formRef.current.reset()
        setSelectedRecordCount("")
        setOpen(false)
      },
      [isAdvancedMode, selectedRecordCount, onAdd],
    )

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-purple-200 bg-purple-50 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950 dark:hover:bg-purple-900"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add Icon
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Instance Icon</DialogTitle>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            {!isAdvancedMode && (
              <div>
                <label className="text-sm font-medium">Type</label>
                <Input name="type" placeholder="Icon type" className="mt-1" required />
              </div>
            )}

            {isAdvancedMode && (
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value="recordCount">
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recordCount">Record Count</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isAdvancedMode ? (
              <div>
                <label className="text-sm font-medium">Position</label>
                <Input name="position" placeholder="Position" className="mt-1" required />
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium">Position</label>
                <Select name="position" defaultValue="left">
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox id="displayIfZero" name="displayIfZero" />
              <label htmlFor="displayIfZero" className="text-sm">
                Display if Zero
              </label>
            </div>

            {isAdvancedMode ? (
              <div>
                <label className="text-sm font-medium">Record Count</label>
                <Select value={selectedRecordCount} onValueChange={setSelectedRecordCount}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={isLoadingLabels ? "Loading..." : "Select count"} />
                  </SelectTrigger>
                  <SelectContent>
                    {evidenceCountLabels.length === 0 && !isLoadingLabels ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">No labels available</div>
                    ) : (
                      evidenceCountLabels.map((label) => (
                        <SelectItem key={label} value={label}>
                          {label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium">Record Count</label>
                <Input name="recordCount" placeholder="Record count field" className="mt-1" />
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Background Color</label>
              <input
                type="color"
                name="backgroundColor"
                defaultValue="#ffffff"
                className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Text Color</label>
              <input
                type="color"
                name="color"
                defaultValue="#000000"
                className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
              />
            </div>

            <div className="flex items-center justify-end gap-4 border-t pt-4">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Add Icon</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    )
  },
)
AddInstanceIconDialog.displayName = "AddInstanceIconDialog"

const PeriodOptionRow = memo(
  ({
    option,
    index,
    onRemove,
    onAddEnumeration,
    onRemoveEnumeration,
  }: {
    option: PeriodOption
    index: number
    onRemove: (index: number) => void
    onAddEnumeration: (periodIndex: number, enumeration: PeriodEnumeration) => void
    onRemoveEnumeration: (periodIndex: number, enumIndex: number) => void
  }) => {
    const [expanded, setExpanded] = useState(false)

    return (
      <div className="rounded-lg border">
        <div className="flex items-center justify-between p-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex-shrink-0 rounded p-1 hover:bg-secondary"
              type="button"
            >
              {expanded ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{option.name}</span>
                <Badge variant="outline" className="text-xs">
                  {option.type}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  Seq: {option.sequence}
                </Badge>
              </div>
              {option.enumeration.length > 0 && (
                <span className="text-xs text-muted-foreground">{option.enumeration.length} enumerations</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <AddEnumerationDialog onAdd={(enumItem) => onAddEnumeration(index, enumItem)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
              className="text-red-600 hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {expanded && option.enumeration.length > 0 && (
          <div className="border-t bg-muted p-3">
            <p className="mb-2 text-sm font-medium text-muted-foreground">Enumerations:</p>
            <div className="space-y-2">
              {option.enumeration.map((enumItem, enumIdx) => (
                <div key={enumIdx} className="flex items-center justify-between rounded border p-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{enumItem.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {enumItem.iconType}
                    </Badge>
                    <div
                      className="h-5 w-5 rounded border"
                      style={{ backgroundColor: enumItem.color }}
                      title={`Color: ${enumItem.color}`}
                    />
                    <span className="text-xs text-muted-foreground">Seq: {enumItem.sequence}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveEnumeration(index, enumIdx)}
                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  },
)
PeriodOptionRow.displayName = "PeriodOptionRow"

export function SharedTimelineTab({
  timelineConfig,
  onConfigChange,
  onSave,
  onRemove,
  isFetching,
  currentAction,
  evidenceCountLabels = [],
  isLoadingLabels = false,
}: SharedTimelineTabProps) {
  const [openSections, setOpenSections] = useState({
    general: true,
    upper: false,
    lower: false,
    instances: false,
  })

  // Determine if we're in advanced mode (Brand Lens)
  const isAdvancedMode = evidenceCountLabels.length > 0 || isLoadingLabels

  const toggleSection = useCallback((section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }, [])

  const handleAddPeriodOption = useCallback(
    (option: PeriodOption) => {
      onConfigChange({
        ...timelineConfig,
        upper: {
          ...timelineConfig.upper,
          periodOptions: [...timelineConfig.upper.periodOptions, option],
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const handleRemovePeriodOption = useCallback(
    (index: number) => {
      onConfigChange({
        ...timelineConfig,
        upper: {
          ...timelineConfig.upper,
          periodOptions: timelineConfig.upper.periodOptions.filter((_, i) => i !== index),
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const handleAddEnumeration = useCallback(
    (optionIndex: number, enumeration: PeriodEnumeration) => {
      const newPeriodOptions = [...timelineConfig.upper.periodOptions]
      newPeriodOptions[optionIndex] = {
        ...newPeriodOptions[optionIndex],
        enumeration: [...newPeriodOptions[optionIndex].enumeration, enumeration],
      }
      onConfigChange({
        ...timelineConfig,
        upper: {
          ...timelineConfig.upper,
          periodOptions: newPeriodOptions,
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const handleRemoveEnumeration = useCallback(
    (optionIndex: number, enumIndex: number) => {
      const newPeriodOptions = [...timelineConfig.upper.periodOptions]
      newPeriodOptions[optionIndex].enumeration = newPeriodOptions[optionIndex].enumeration.filter(
        (_, i) => i !== enumIndex,
      )
      onConfigChange({
        ...timelineConfig,
        upper: {
          ...timelineConfig.upper,
          periodOptions: newPeriodOptions,
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const handleAddInstanceStyleValue = useCallback(
    (value: InstanceStyleValue) => {
      onConfigChange({
        ...timelineConfig,
        instances: {
          ...timelineConfig.instances,
          instanceStyle: {
            ...timelineConfig.instances.instanceStyle,
            values: [...timelineConfig.instances.instanceStyle.values, value],
          },
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const handleRemoveInstanceStyleValue = useCallback(
    (index: number) => {
      onConfigChange({
        ...timelineConfig,
        instances: {
          ...timelineConfig.instances,
          instanceStyle: {
            ...timelineConfig.instances.instanceStyle,
            values: timelineConfig.instances.instanceStyle.values.filter((_, i) => i !== index),
          },
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const handleAddInstanceIcon = useCallback(
    (icon: InstanceIcon) => {
      onConfigChange({
        ...timelineConfig,
        instances: {
          ...timelineConfig.instances,
          instanceIcons: [...timelineConfig.instances.instanceIcons, icon],
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const handleRemoveInstanceIcon = useCallback(
    (index: number) => {
      onConfigChange({
        ...timelineConfig,
        instances: {
          ...timelineConfig.instances,
          instanceIcons: timelineConfig.instances.instanceIcons.filter((_, i) => i !== index),
        },
      })
    },
    [timelineConfig, onConfigChange],
  )

  const showSaveLoading = isFetching && (currentAction === "SAVE_SECTION" || !currentAction)
  const showRemoveLoading = isFetching && currentAction === "REMOVE_SECTION"

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full space-y-4">
          {/* General Settings */}
          <div className="rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("general")}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-muted"
            >
              <h3 className="text-lg font-medium">General Timeline Settings</h3>
              {openSections.general ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
            {openSections.general && (
              <div className="border-t p-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="useUpper"
                      checked={timelineConfig.general.useUpper}
                      onCheckedChange={(checked) =>
                        onConfigChange({
                          ...timelineConfig,
                          general: { ...timelineConfig.general, useUpper: checked === true },
                        })
                      }
                    />
                    <label htmlFor="useUpper" className="cursor-pointer text-sm">
                      Use Upper Timeline
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="useLower"
                      checked={timelineConfig.general.useLower}
                      onCheckedChange={(checked) =>
                        onConfigChange({
                          ...timelineConfig,
                          general: { ...timelineConfig.general, useLower: checked === true },
                        })
                      }
                    />
                    <label htmlFor="useLower" className="cursor-pointer text-sm">
                      Use Lower Timeline
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="useInstances"
                      checked={timelineConfig.general.useInstances}
                      onCheckedChange={(checked) =>
                        onConfigChange({
                          ...timelineConfig,
                          general: { ...timelineConfig.general, useInstances: checked === true },
                        })
                      }
                    />
                    <label htmlFor="useInstances" className="cursor-pointer text-sm">
                      Use Instances
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Upper Timeline Settings */}
          <div className="rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("upper")}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-muted"
            >
              <h3 className="text-lg font-medium">Upper Timeline Settings</h3>
              {openSections.upper ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
            {openSections.upper && (
              <div className="space-y-4 border-t p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">Period Name (Singular)</label>
                    <Input
                      value={timelineConfig.upper.periodNameSingular}
                      onChange={(e) =>
                        onConfigChange({
                          ...timelineConfig,
                          upper: { ...timelineConfig.upper, periodNameSingular: e.target.value },
                        })
                      }
                      placeholder="e.g., Period"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Period Name (Plural)</label>
                    <Input
                      value={timelineConfig.upper.periodNamePlural}
                      onChange={(e) =>
                        onConfigChange({
                          ...timelineConfig,
                          upper: { ...timelineConfig.upper, periodNamePlural: e.target.value },
                        })
                      }
                      placeholder="e.g., Periods"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Period Overlap Months</label>
                    <Input
                      type="number"
                      value={timelineConfig.upper.periodOverlapMonths}
                      onChange={(e) =>
                        onConfigChange({
                          ...timelineConfig,
                          upper: { ...timelineConfig.upper, periodOverlapMonths: parseInt(e.target.value) || 0 },
                        })
                      }
                      min="0"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="allowManualPeriodDateAdjustment"
                      checked={timelineConfig.upper.allowManualPeriodDateAdjustment}
                      onCheckedChange={(checked) =>
                        onConfigChange({
                          ...timelineConfig,
                          upper: { ...timelineConfig.upper, allowManualPeriodDateAdjustment: checked === true },
                        })
                      }
                    />
                    <label htmlFor="allowManualPeriodDateAdjustment" className="cursor-pointer text-sm">
                      Allow Manual Period Date Adjustment
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="allowManualPeriodOptionsAdjustment"
                      checked={timelineConfig.upper.allowManualPeriodOptionsAdjustment}
                      onCheckedChange={(checked) =>
                        onConfigChange({
                          ...timelineConfig,
                          upper: { ...timelineConfig.upper, allowManualPeriodOptionsAdjustment: checked === true },
                        })
                      }
                    />
                    <label htmlFor="allowManualPeriodOptionsAdjustment" className="cursor-pointer text-sm">
                      Allow Manual Period Options Adjustment
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="periodOverlapMonthsToEOM"
                      checked={timelineConfig.upper.periodOverlapMonthsToEOM}
                      onCheckedChange={(checked) =>
                        onConfigChange({
                          ...timelineConfig,
                          upper: { ...timelineConfig.upper, periodOverlapMonthsToEOM: checked === true },
                        })
                      }
                    />
                    <label htmlFor="periodOverlapMonthsToEOM" className="cursor-pointer text-sm">
                      Period Overlap Months to End of Month
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="disableOverlapMonths"
                      checked={timelineConfig.upper.disableOverlapMonths}
                      onCheckedChange={(checked) =>
                        onConfigChange({
                          ...timelineConfig,
                          upper: { ...timelineConfig.upper, disableOverlapMonths: checked === true },
                        })
                      }
                    />
                    <label htmlFor="disableOverlapMonths" className="cursor-pointer text-sm">
                      Disable Overlap Months
                    </label>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-medium">
                      Period Options ({timelineConfig.upper.periodOptions.length})
                    </h4>
                    <AddPeriodOptionDialog onAdd={handleAddPeriodOption} />
                  </div>
                  <div className="space-y-2">
                    {timelineConfig.upper.periodOptions.length > 0 ? (
                      timelineConfig.upper.periodOptions.map((option, index) => (
                        <PeriodOptionRow
                          key={`${option.name}-${index}`}
                          option={option}
                          index={index}
                          onRemove={handleRemovePeriodOption}
                          onAddEnumeration={handleAddEnumeration}
                          onRemoveEnumeration={handleRemoveEnumeration}
                        />
                      ))
                    ) : (
                      <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                        <p className="text-sm">No period options configured</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Lower Timeline Settings */}
          <div className="rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("lower")}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-muted"
            >
              <h3 className="text-lg font-medium">Lower Timeline Settings</h3>
              {openSections.lower ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
            {openSections.lower && (
              <div className="border-t p-4">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="showRecordClusters"
                    checked={timelineConfig.lower.showRecordClusters}
                    onCheckedChange={(checked) =>
                      onConfigChange({
                        ...timelineConfig,
                        lower: { ...timelineConfig.lower, showRecordClusters: checked === true },
                      })
                    }
                  />
                  <label htmlFor="showRecordClusters" className="cursor-pointer text-sm">
                    Show Record Clusters
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Instance Settings */}
          <div className="rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("instances")}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-muted"
            >
              <h3 className="text-lg font-medium">Instance Settings</h3>
              {openSections.instances ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
            {openSections.instances && (
              <div className="space-y-4 border-t p-4">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="showSequenceNumber"
                    checked={timelineConfig.instances.showSequenceNumber}
                    onCheckedChange={(checked) =>
                      onConfigChange({
                        ...timelineConfig,
                        instances: { ...timelineConfig.instances, showSequenceNumber: checked === true },
                      })
                    }
                  />
                  <label htmlFor="showSequenceNumber" className="cursor-pointer text-sm">
                    Show Sequence Number
                  </label>
                </div>

                {/* Instance Style - Simple Mode (UIConfig) */}
                {!isAdvancedMode && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium">Style Type</label>
                      <Input
                        value={timelineConfig.instances.instanceStyle.type}
                        onChange={(e) =>
                          onConfigChange({
                            ...timelineConfig,
                            instances: {
                              ...timelineConfig.instances,
                              instanceStyle: { ...timelineConfig.instances.instanceStyle, type: e.target.value },
                            },
                          })
                        }
                        placeholder="e.g., instanceTag"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Instance Tag</label>
                      <Input
                        value={timelineConfig.instances.instanceStyle.instanceTag}
                        onChange={(e) =>
                          onConfigChange({
                            ...timelineConfig,
                            instances: {
                              ...timelineConfig.instances,
                              instanceStyle: { ...timelineConfig.instances.instanceStyle, instanceTag: e.target.value },
                            },
                          })
                        }
                        placeholder="Tag name"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Default Background</label>
                      <input
                        type="color"
                        value={timelineConfig.instances.instanceStyle.defaultBackground}
                        onChange={(e) =>
                          onConfigChange({
                            ...timelineConfig,
                            instances: {
                              ...timelineConfig.instances,
                              instanceStyle: {
                                ...timelineConfig.instances.instanceStyle,
                                defaultBackground: e.target.value,
                              },
                            },
                          })
                        }
                        className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Default Text Color</label>
                      <input
                        type="color"
                        value={timelineConfig.instances.instanceStyle.defaultColour}
                        onChange={(e) =>
                          onConfigChange({
                            ...timelineConfig,
                            instances: {
                              ...timelineConfig.instances,
                              instanceStyle: {
                                ...timelineConfig.instances.instanceStyle,
                                defaultColour: e.target.value,
                              },
                            },
                          })
                        }
                        className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
                      />
                    </div>
                  </div>
                )}

                {/* Instance Style - Advanced Mode (Brand Lens) */}
                {isAdvancedMode && (
                  <div className="rounded-lg border bg-muted p-4">
                    <h4 className="mb-4 font-medium">Instance Style</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Default Background</label>
                          <input
                            type="color"
                            value={timelineConfig.instances.instanceStyle.defaultBackground}
                            onChange={(e) =>
                              onConfigChange({
                                ...timelineConfig,
                                instances: {
                                  ...timelineConfig.instances,
                                  instanceStyle: {
                                    ...timelineConfig.instances.instanceStyle,
                                    defaultBackground: e.target.value,
                                  },
                                },
                              })
                            }
                            className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium">Default Color</label>
                          <input
                            type="color"
                            value={timelineConfig.instances.instanceStyle.defaultColour}
                            onChange={(e) =>
                              onConfigChange({
                                ...timelineConfig,
                                instances: {
                                  ...timelineConfig.instances,
                                  instanceStyle: {
                                    ...timelineConfig.instances.instanceStyle,
                                    defaultColour: e.target.value,
                                  },
                                },
                              })
                            }
                            className="mt-1 h-10 w-full cursor-pointer rounded border border-gray-300"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium">Type</label>
                        <Select
                          value={timelineConfig.instances.instanceStyle.type}
                          onValueChange={(value) =>
                            onConfigChange({
                              ...timelineConfig,
                              instances: {
                                ...timelineConfig.instances,
                                instanceStyle: {
                                  ...timelineConfig.instances.instanceStyle,
                                  type: value,
                                },
                              },
                            })
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="instanceTag">Instance Tag</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-sm font-medium">Instance Tag</label>
                        <Input
                          value={timelineConfig.instances.instanceStyle.instanceTag}
                          onChange={(e) =>
                            onConfigChange({
                              ...timelineConfig,
                              instances: {
                                ...timelineConfig.instances,
                                instanceStyle: {
                                  ...timelineConfig.instances.instanceStyle,
                                  instanceTag: e.target.value,
                                },
                              },
                            })
                          }
                          placeholder="Instance tag"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-medium">
                      Style Values ({timelineConfig.instances.instanceStyle.values.length})
                    </h4>
                    <AddInstanceStyleValueDialog onAdd={handleAddInstanceStyleValue} />
                  </div>
                  <div className="space-y-2">
                    {timelineConfig.instances.instanceStyle.values.length > 0 ? (
                      timelineConfig.instances.instanceStyle.values.map((value, index) => (
                        <div key={index} className="flex items-center justify-between rounded border bg-muted p-3">
                          <div className="flex items-center gap-3">
                            <span className="font-medium">{value.value}</span>
                            <div
                              className="h-6 w-6 rounded border"
                              style={{ backgroundColor: value.background }}
                              title={`Background: ${value.background}`}
                            />
                            <div
                              className="h-6 w-6 rounded border"
                              style={{ backgroundColor: value.color }}
                              title={`Text: ${value.color}`}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveInstanceStyleValue(index)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                        <p className="text-sm">No style values configured</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-medium">
                      Instance Icons ({timelineConfig.instances.instanceIcons.length})
                    </h4>
                    <AddInstanceIconDialog
                      onAdd={handleAddInstanceIcon}
                      evidenceCountLabels={evidenceCountLabels}
                      isLoadingLabels={isLoadingLabels}
                    />
                  </div>
                  <div className="space-y-2">
                    {timelineConfig.instances.instanceIcons.length > 0 ? (
                      timelineConfig.instances.instanceIcons.map((icon, index) => (
                        <div key={index} className="flex items-center justify-between rounded border bg-muted p-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{icon.type}</span>
                              <Badge variant="outline" className="text-xs">
                                {icon.position}
                              </Badge>
                              {icon.displayIfZero && (
                                <Badge variant="secondary" className="text-xs">
                                  Show Zero
                                </Badge>
                              )}
                            </div>
                            {icon.recordCount.length > 0 && (
                              <p className="mt-1 text-xs text-muted-foreground">Count: {icon.recordCount}</p>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveInstanceIcon(index)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                        <p className="text-sm">No instance icons configured</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save and Remove Buttons */}
      <div className="flex-shrink-0 border-t p-4">
        <div className="mx-auto flex w-full items-center gap-3">
          <Button
            onClick={() => void onSave(timelineConfig)}
            disabled={isFetching}
            className="flex-1 bg-info hover:bg-info/90"
          >
            {showSaveLoading && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
            <SaveIcon className="h-4 w-4" />
            Save Timeline
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={isFetching}
              >
                {showRemoveLoading ? (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircleIcon className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Remove Section</span>
                <span className="sm:hidden">Remove</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-80">
              <div className="space-y-3">
                <p className="text-sm font-medium">Remove Timeline Configuration?</p>
                <p className="text-sm text-muted-foreground">
                  This will remove the entire timeline configuration from this level.
                </p>
                <p className="text-sm font-medium text-red-600 dark:text-red-400">This action cannot be undone.</p>
                <div className="flex items-center justify-end gap-3">
                  <PopoverClose asChild>
                    <Button type="button" variant="outline" size="sm">
                      Cancel
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => void onRemove()}
                      disabled={isFetching}
                    >
                      Remove
                    </Button>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
}
